import React from "react";
import { withTranslation } from "react-i18next";
import { captureException } from "../sentry";

// Nach einem Deploy existieren die alten, gehashten Chunks nicht mehr. Ein noch offener
// Tab bekommt dann beim Lazy-Laden einen ChunkLoadError — und React.lazy cached die
// abgelehnte Promise, d. h. "Nochmal versuchen" würde denselben Fehler endlos werfen.
// Einzige echte Abhilfe: Seite neu laden (holt die neue index.html mit neuen Hashes).
const CHUNK_ERROR_RE = /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const RELOAD_FLAG = "aof_chunk_reload_at";
const RELOAD_WINDOW_MS = 30_000; // innerhalb dieses Fensters nicht erneut automatisch neu laden

function isChunkLoadError(error) {
  if (!error) return false;
  return CHUNK_ERROR_RE.test(`${error.name || ""} ${error.message || ""}`);
}

// Lädt die Seite höchstens einmal pro RELOAD_WINDOW_MS automatisch neu (Schutz vor
// Reload-Schleifen, falls der Chunk auch nach dem Neuladen fehlt, z. B. offline).
function tryAutoReload() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage nicht verfügbar (z. B. Safari privat, alt) → trotzdem einmal neu laden
  }
  window.location.reload();
  return true;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && tryAutoReload()) return;
    // No-Op ohne REACT_APP_SENTRY_DSN; vor dem (verzögerten) Laden von Sentry wird
    // der Fehler zwischengespeichert und nachgereicht (siehe src/sentry.js).
    captureException(error);
  }

  componentDidUpdate(prevProps) {
    // resetKey (z. B. der Pfad) ändert sich → Fehlerzustand verlassen, damit die
    // Navigation zu einer anderen Seite nicht weiter den Fehlerbildschirm zeigt.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, isChunkError: false });
    }
  }

  handleRetry = () => {
    if (this.state.isChunkError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, isChunkError: false });
  };

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <div role="alert" style={{ textAlign: "center", padding: 60, fontFamily: "sans-serif" }}>
          <div style={{ fontSize: 52 }} aria-hidden="true">💨</div>
          <h2 style={{ marginBottom: 12 }}>
            {this.state.isChunkError ? t("errorBoundary.updateTitle") : t("errorBoundary.title")}
          </h2>
          <button
            onClick={this.handleRetry}
            style={{
              padding: "10px 24px", borderRadius: 999, border: "none",
              background: "#4caf50", color: "#0a3317", cursor: "pointer", fontWeight: 700,
            }}
          >
            {this.state.isChunkError ? t("errorBoundary.reload") : t("errorBoundary.retry")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
